import { useState, useEffect } from 'react';
import moment from 'moment';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import TableBody from '@mui/material/TableBody';
import Chip from '@mui/material/Chip';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import IconButton from '@mui/material/IconButton';
import SendIcon from '@mui/icons-material/Send';
import EditIcon from '@mui/icons-material/Edit';
import CircularProgress from '@mui/material/CircularProgress';
import { useNavigate } from 'react-router-dom';


import { API } from 'services';
import { Title, TraxxContainer, SortedHeaderComponent } from 'components';
import { enqueueSnackbar } from 'notistack';

/* =========================
   TABLE HEADER CONFIG
========================= */
const header = [
  { id: 'id', label: 'No.', sortable: true },
  { id: 'entity', label: 'Name', sortable: true },
  { id: 'country', label: 'Country', sortable: true },
  { id: 'clientType', label: 'Client Type', sortable: true },
  { id: 'director_ubo', label: 'Director/UBO' },
  { id: 'finalRisk', label: 'Risk Rating', sortable: true },
  { id: 'status', label: 'Action', sortable: true },
  { id: 'zohoStatus', label: 'Sign Status', sortable: true },
  { id: 'document', label: 'Document' },
  { id: 'send', label: 'Send' },
  { id: 'createdOn', label: 'Date of Creation', sortable: true },
  { id: 'edit', label: 'Edit' },
];

const COLOR = {
  low: 'success',
  medL: 'warning',
  medH: 'warning',
  high: 'error',
  maintain: 'info',
  establish: 'success',
  decline: 'primary',
  terminate: 'error',
  gray: 'warning',
  black: 'error',
  others: 'error',
};

/* =========================
   BODY COMPONENT
========================= */
const BodyComponent = ({ riskList }) => {
  const [loading, setLoading] = useState({});
  const navigate = useNavigate();

  const handleSendZoho = async (id, requestID) => {
    setLoading({ [id]: true });

    const result = await API.Misc.send_zoho_doc(requestID);
    if (result?.data?.result?.status === 'failure') {
      enqueueSnackbar('Unable to send document!', { variant: 'error' });
    } else {
      enqueueSnackbar('Document successfully sent!', { variant: 'success' });
    }

    setLoading({ [id]: false });
  };

  const progress = (status) => {
    if (status === 'completed') return <Chip label="Complete" color="success" />;
    if (status === 'inprogress')
      return <Chip label="In Progress" variant="outlined" color="warning" />;
    return <Chip label="Pending" variant="outlined" color="info" />;
  };

  return (
    <TableBody>
      {riskList.map((item, index) => {
        const {
          id,
          originalRiskId,
          entity,
          country,
          clientType,
          finalRisk,
          status,
          requestID,
          zohoStatus,
          createdOn,
          people,
        } = item;


        console.log(item)
        const formattedDate = moment(createdOn).format('lll');

        let peopleType = '';
        if (people?.includes('black')) peopleType = 'black';
        else if (people?.includes('others')) peopleType = 'others';
        else if (people?.includes('gray')) peopleType = 'gray';

        return (
          <TableRow key={id}>
            <TableCell>{originalRiskId}</TableCell>
            <TableCell>{entity}</TableCell>
            <TableCell>{country}</TableCell>
            <TableCell>{clientType === 'p' ? 'Partner' : 'Merchant'}</TableCell>

            <TableCell>
              {peopleType && (
                <Chip
                  label={peopleType}
                  color={COLOR[peopleType]}
                  variant="outlined"
                />
              )}
            </TableCell>

            <TableCell>
              <Chip
                label={
                  finalRisk === 'medH'
                    ? 'med/high'
                    : finalRisk === 'medL'
                    ? 'low/med'
                    : finalRisk
                }
                color={COLOR[finalRisk]}
                variant="outlined"
              />
            </TableCell>

            <TableCell>
              <Chip label={status} color={COLOR[status]} variant="outlined" />
            </TableCell>

            <TableCell>{progress(zohoStatus)}</TableCell>

            <TableCell sx={{ minWidth: 220 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  justifyContent: "center",
                }}
              >
                <IconButton
                  disabled={!requestID}
                  onClick={() => API.Misc.fetch_zoho_doc(requestID)}
                  size="small"
                >
                  <PictureAsPdfIcon />
                </IconButton>

                {requestID && (
                  <span
                    style={{
                      fontSize: "0.75rem",
                      color: "#666",
                      fontFamily: "monospace",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {requestID}
                  </span>
                )}
              </div>
            </TableCell>

            <TableCell>
              {loading[id] ? (
                <CircularProgress size="1rem" />
              ) : (
                <IconButton
                  disabled={!!zohoStatus}
                  onClick={() => handleSendZoho(id, requestID)}
                >
                  <SendIcon />
                </IconButton>
              )}
            </TableCell>

            <TableCell>{formattedDate}</TableCell>

            <TableCell>
              <IconButton
                onClick={() =>
                  navigate(
                    `/compliance/risk-assessment/edit?id=${id}&data=${index}`
                  )
                }
              >
                <EditIcon />
              </IconButton>
            </TableCell>
          </TableRow>
        );
      })}
    </TableBody>
  );
};

/* =========================
   MAIN COMPONENT
========================= */
const ViewAssessment = () => {
  const [riskList, setRiskList] = useState([]);
  const [sortBy, setSortBy] = useState('id');
  const [sortOrder, setSortOrder] = useState('desc');

  useEffect(() => {
    API.Compliance.Risk.fetch_all_risk().then(setRiskList);
  }, []);
  const [filters, setFilters] = useState({
    finalRisk: '',
    status: '',
    clientType: '',
    zohoStatus: '',
  });

  const handleSort = (key) => {
    if (sortBy === key) {
      setSortOrder((p) => (p === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortOrder('asc');
    }
  };

  const filteredList = riskList.filter((item) => {
    if (filters.finalRisk && item.finalRisk !== filters.finalRisk) return false;
    if (filters.status && item.status !== filters.status) return false;
    if (filters.clientType && item.clientType !== filters.clientType) return false;

    if (filters.zohoStatus) {
      if (filters.zohoStatus === "pending") {
        if (item.zohoStatus !== null && item.zohoStatus !== "") return false;
      } else {
        if (item.zohoStatus !== filters.zohoStatus) return false;
      }
    }

    return true;
  });


  const sortedList = [...filteredList].sort((a, b) => {
    //console.log(filteredList.length)
    const aVal = a[sortBy];
    const bVal = b[sortBy];

    if (aVal == null) return 1;
    if (bVal == null) return -1;

    // ✅ FIX: numeric sort for ID
    if (sortBy === 'id') {
      return sortOrder === 'asc'
        ? Number(aVal) - Number(bVal)
        : Number(bVal) - Number(aVal);
    }

    // date sort
    if (sortBy === 'createdOn') {
      return sortOrder === 'asc'
        ? new Date(aVal) - new Date(bVal)
        : new Date(bVal) - new Date(aVal);
    }

    // fallback string sort
    return sortOrder === 'asc'
      ? String(aVal).localeCompare(String(bVal))
      : String(bVal).localeCompare(String(aVal));
  });


  return (
    <>
      <Title text="View Assessments List" />

      <TraxxContainer
        component={
          <>
            <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
              <select onChange={(e) => setFilters(f => ({ ...f, finalRisk: e.target.value }))}>
                <option value="">All Risks</option>
                <option value="low">Low</option>
                <option value="medL">Low/Med</option>
                <option value="medH">Med/High</option>
                <option value="high">High</option>
              </select>

              <select onChange={(e) => setFilters(f => ({ ...f, status: e.target.value }))}>
                <option value="">All Actions</option>
                <option value="maintain">Maintain</option>
                <option value="establish">Establish</option>
                <option value="decline">Decline</option>
                <option value="terminate">Terminate</option>
              </select>

              <select onChange={(e) => setFilters(f => ({ ...f, clientType: e.target.value }))}>
                <option value="">All Types</option>
                <option value="p">Partner</option>
                <option value="m">Merchant</option>
              </select>

              <select onChange={(e) => setFilters(f => ({ ...f, zohoStatus: e.target.value }))}>
                <option value="">All Sign Status</option>
                <option value="pending">Pending</option>
                <option value="completed">Completed</option>
              </select>

            </div>

            <TableContainer>
              <Table>
                <SortedHeaderComponent
                  header={header}
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  onSort={handleSort}
                />
                <BodyComponent riskList={sortedList} />
              </Table>
            </TableContainer>
          </>
        }
      />
    </>
  );
};

export default ViewAssessment;
